// src/pages/affiliate/ProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import { getBaseUrl } from '../../config/api';
import { formatCommissionPercentForDisplay } from '../../utils/affiliateFormat';
import '../../styles/AffiliateProfile.css';

interface AffiliateProfile {
  _id: string;
  affiliateId: string;
  personalInfo: {
    fullName: string;
    email: string;
    phone: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  };
  socialMedia: {
    platform: string;
    handle: string;
    followerCount: number;
    profileUrl: string;
  };
  status: string;
  tier: string;
  commissionRate: number;
  referralCode: string;
  referralLink: string;
  createdAt: string;
  payoutInfo?: {
    method: string;
    paypalEmail?: string;
  };
}

const AffiliateProfilePage: React.FC = () => {
  const { showSnackbar } = useUI();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<AffiliateProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    socialMedia: {
      platform: '',
      handle: '',
      followerCount: 0,
      profileUrl: ''
    }
  });
  const [paymentMethod, setPaymentMethod] = useState({
    method: 'paypal',
    paypalEmail: ''
  });
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to view profile', 'error');
        navigate('/affiliate/login');
        return;
      }

      const response = await fetch(`${getBaseUrl()}/v1/affiliate/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProfile(data.data);
        setFormData({
          fullName: data.data.personalInfo?.fullName || '',
          phone: data.data.personalInfo?.phone || '',
          address: {
            street: data.data.personalInfo?.address?.street || '',
            city: data.data.personalInfo?.address?.city || '',
            state: data.data.personalInfo?.address?.state || '',
            zipCode: data.data.personalInfo?.address?.zipCode || '',
            country: data.data.personalInfo?.address?.country || ''
          },
          socialMedia: {
            platform: data.data.socialMedia?.platform || '',
            handle: data.data.socialMedia?.handle || '',
            followerCount: data.data.socialMedia?.followerCount || 0,
            profileUrl: data.data.socialMedia?.profileUrl || ''
          }
        });
        
        // Set payment method data
        if (data.data.payoutInfo) {
          setPaymentMethod({
            method: 'paypal',
            paypalEmail: data.data.payoutInfo.paypalEmail || ''
          });
        }
      } else {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('affiliateToken');
          sessionStorage.removeItem('affiliateToken');
          showSnackbar('Session expired. Please log in again.', 'error');
          navigate('/affiliate/login');
          return;
        }
        showSnackbar(data.message || 'Failed to load profile', 'error');
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      showSnackbar('An error occurred loading profile', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to update profile', 'error');
        return;
      }

      const response = await fetch(`${getBaseUrl()}/v1/affiliate/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          phone: formData.phone,
          address: formData.address,
          socialMedia: formData.socialMedia
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showSnackbar('Profile updated successfully!', 'success');
        setProfile(data.data);
      } else {
        showSnackbar(data.message || 'Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showSnackbar('An error occurred updating profile', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [field]: value
        }
      }));
    } else if (name.startsWith('socialMedia.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [field]: field === 'followerCount' ? parseInt(value) || 0 : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePaymentMethodSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSavingPayment(true);
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to update payment method', 'error');
        return;
      }

      if (!paymentMethod.paypalEmail) {
        showSnackbar('Please provide your PayPal email', 'error');
        setIsSavingPayment(false);
        return;
      }

      const response = await fetch(`${getBaseUrl()}/v1/affiliate/payout-method`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          method: 'paypal',
          paypalEmail: paymentMethod.paypalEmail
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showSnackbar('Payment method updated successfully!', 'success');
        setShowPaymentSection(false);
        // Refresh profile to get updated data
        fetchProfile();
      } else {
        showSnackbar(data.message || 'Failed to update payment method', 'error');
      }
    } catch (error) {
      console.error('Payment method update error:', error);
      showSnackbar('An error occurred updating payment method', 'error');
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPaymentMethod(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="affiliate-profile__loading">
        <div className="spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="affiliate-profile__error">
        <p>Failed to load profile</p>
        <button onClick={fetchProfile} className="btn-primary">Retry</button>
      </div>
    );
  }

  return (
    <div className="affiliate-profile">
      <div className="affiliate-profile__header">
        <div>
          <h1>Edit Profile</h1>
          <p>Update your personal information and social media details</p>
        </div>
        <button onClick={() => navigate('/affiliate/dashboard')} className="btn-secondary">
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <form onSubmit={handleSubmit} className="affiliate-profile__form">
        {/* Personal Information */}
        <div className="form-section">
          <h2>
            <i className="fas fa-user"></i>
            Personal Information
          </h2>
          
          <div className="form-group">
            <label htmlFor="fullName">Full Name *</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={profile.personalInfo.email}
              disabled
              className="form-input form-input--disabled"
            />
            <small>Email cannot be changed. Contact support if you need to update it.</small>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="form-input"
              placeholder="+1234567890"
            />
          </div>
        </div>

        {/* Address */}
        <div className="form-section">
          <h2>
            <i className="fas fa-map-marker-alt"></i>
            Address
          </h2>
          
          <div className="form-group">
            <label htmlFor="address.street">Street Address</label>
            <input
              type="text"
              id="address.street"
              name="address.street"
              value={formData.address.street}
              onChange={handleInputChange}
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.city">City</label>
              <input
                type="text"
                id="address.city"
                name="address.city"
                value={formData.address.city}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.state">State/Province</label>
              <input
                type="text"
                id="address.state"
                name="address.state"
                value={formData.address.state}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.zipCode">ZIP/Postal Code</label>
              <input
                type="text"
                id="address.zipCode"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.country">Country</label>
              <input
                type="text"
                id="address.country"
                name="address.country"
                value={formData.address.country}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="form-section">
          <h2>
            <i className="fas fa-share-alt"></i>
            Social Media
          </h2>
          
          <div className="form-group">
            <label htmlFor="socialMedia.platform">Platform</label>
            <select
              id="socialMedia.platform"
              name="socialMedia.platform"
              value={formData.socialMedia.platform}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="">Select Platform</option>
              <option value="Instagram">Instagram</option>
              <option value="Facebook">Facebook</option>
              <option value="YouTube">YouTube</option>
              <option value="TikTok">TikTok</option>
              <option value="Twitter">Twitter</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="socialMedia.handle">Handle/Username</label>
            <input
              type="text"
              id="socialMedia.handle"
              name="socialMedia.handle"
              value={formData.socialMedia.handle}
              onChange={handleInputChange}
              className="form-input"
              placeholder="@username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="socialMedia.followerCount">Follower Count</label>
            <input
              type="number"
              id="socialMedia.followerCount"
              name="socialMedia.followerCount"
              value={formData.socialMedia.followerCount}
              onChange={handleInputChange}
              className="form-input"
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="socialMedia.profileUrl">Profile URL</label>
            <input
              type="url"
              id="socialMedia.profileUrl"
              name="socialMedia.profileUrl"
              value={formData.socialMedia.profileUrl}
              onChange={handleInputChange}
              className="form-input"
              placeholder="https://..."
            />
          </div>
        </div>

        {/* Payment Method */}
        <div className="form-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2>
              <i className="fas fa-credit-card"></i>
              Payment Method
            </h2>
            <button
              type="button"
              onClick={() => setShowPaymentSection(!showPaymentSection)}
              className="btn-secondary"
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              {showPaymentSection ? 'Cancel' : paymentMethod.paypalEmail ? 'Update PayPal Email' : 'Set PayPal Email'}
            </button>
          </div>

          {!showPaymentSection ? (
            <div className="info-grid">
              <div className="info-item">
                <label>Current Method</label>
                <p>
                  {paymentMethod.paypalEmail ? (
                    <>
                      <i className="fab fa-paypal" style={{ marginRight: '0.5rem' }}></i>
                      PayPal: {paymentMethod.paypalEmail}
                    </>
                  ) : (
                    <span className="badge badge-warning">PayPal email not set</span>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handlePaymentMethodSubmit} style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label htmlFor="paypalEmail">PayPal Email *</label>
                <input
                  type="email"
                  id="paypalEmail"
                  name="paypalEmail"
                  value={paymentMethod.paypalEmail}
                  onChange={handlePaymentMethodChange}
                  className="form-input"
                  placeholder="your@email.com"
                  required
                />
                <small>Payouts are sent to this PayPal email only.</small>
              </div>

              <div className="form-actions" style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowPaymentSection(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingPayment || !paymentMethod.paypalEmail}
                  className="btn-primary"
                >
                  {isSavingPayment ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Save Payment Method
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Account Info (Read-only) */}
        <div className="form-section">
          <h2>
            <i className="fas fa-info-circle"></i>
            Account Information
          </h2>
          
          <div className="info-grid">
            <div className="info-item">
              <label>Referral Agent ID</label>
              <p>{profile.affiliateId}</p>
            </div>
            <div className="info-item">
              <label>Referral Code</label>
              <p><code>{profile.referralCode}</code></p>
            </div>
            <div className="info-item">
              <label>Status</label>
              <p><span className={`badge badge-${profile.status}`}>{profile.status}</span></p>
            </div>
            <div className="info-item">
              <label>Tier</label>
              <p><span className="badge badge-info">{profile.tier}</span></p>
            </div>
            <div className="info-item">
              <label>Commission Rate</label>
              <p>{formatCommissionPercentForDisplay(profile.commissionRate)}</p>
            </div>
            <div className="info-item">
              <label>Joined Date</label>
              <p>{new Date(profile.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/affiliate/dashboard')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AffiliateProfilePage;

